import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className="w-3 h-3 flex items-center justify-center">
          <i
            className={`text-[10px] ${
              star <= Math.floor(rating)
                ? 'ri-star-fill text-amber-400'
                : star - 0.5 <= rating
                ? 'ri-star-half-line text-amber-400'
                : 'ri-star-line text-foreground-300'
            }`}
          ></i>
        </span>
      ))}
    </div>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'remen-tuijian');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeTab),
    [activeTab]
  );

  const categoryComics = useMemo(
    () => getComicsByCategory(activeTab),
    [activeTab]
  );

  const handleTabChange = (newSlug: string) => {
    setActiveTab(newSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const categoryPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '分类'} - 韩漫大全`,
    description: currentCategory?.description || '韩国漫画分类浏览',
    url: `https://www.hanman123.com/category/${activeTab}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫大全',
      url: 'https://www.hanman123.com/',
    },
  };

  return (
    <>
      <PageMeta
        title={`${currentCategory?.name || '分类浏览'} - 韩漫大全 - 韩国漫画免费在线观看`}
        description={currentCategory?.description || '浏览各种类型的韩国漫画，包括恋爱、动作、奇幻、悬疑、搞笑等多种类型，免费在线观看无删减漫画。'}
        keywords={`韩国漫画,${currentCategory?.name || ''},韩漫大全,无删减漫画免费在线观看`}
        canonicalUrl={`https://www.hanman123.com/category/${activeTab}`}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryPageSchema) }}
        />

        <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '分类浏览' },
            ]}
          />

          {/* Category Header */}
          <div className="py-8">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
              {currentCategory?.name || '分类浏览'}
            </h1>
            <p className="text-sm text-foreground-500">{currentCategory?.description}</p>
          </div>

          {/* Category Tabs */}
          <div className="mb-8">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Comics Grid */}
          {categoryComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 pb-16" data-product-shop>
              {categoryComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-100">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - ${currentCategory?.name}韩国漫画`}
                      title={`${comic.title} - ${currentCategory?.name}韩国漫画免费在线观看`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-md">
                        {comic.status}
                      </span>
                    </div>
                    <div className="absolute bottom-2 right-2">
                      <span className="px-2 py-0.5 bg-black/60 text-white text-xs rounded-md">
                        {comic.chapters}话
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-bold font-heading text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mb-1.5">{comic.author}</p>
                  <StarRating rating={comic.rating} />
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-2xl">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </span>
              <p className="text-foreground-500 text-sm">该分类下暂无漫画作品</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}